import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createVendor,
  deleteVendor,
  fetchVendors,
  receiveStock,
  updateVendor,
  type CreateVendorPayload,
  type ListVendorsQuery,
  type ReceiveStockPayload,
  type UpdateVendorPayload,
} from '@/services/api/retailVendors.api';

export function useRetailVendors(query: ListVendorsQuery, enabled = true) {
  return useQuery({
    queryKey: ['retail-vendors', query],
    queryFn: () => fetchVendors(query),
    staleTime: 30_000,
    enabled: enabled && !!query.propertyId,
  });
}

export function useCreateRetailVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateVendorPayload) => createVendor(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['retail-vendors'] });
    },
  });
}

export function useUpdateRetailVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateVendorPayload }) =>
      updateVendor(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['retail-vendors'] });
    },
  });
}

export function useDeleteRetailVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteVendor(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['retail-vendors'] });
    },
  });
}

export function useReceiveStock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ReceiveStockPayload) => receiveStock(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['retail-products'] });
      void queryClient.invalidateQueries({ queryKey: ['retail-stock'] });
      void queryClient.invalidateQueries({ queryKey: ['retail-reports', 'activity'] });
    },
  });
}
